import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { notificationJobAPI } from '../services/api';

export type NotificationJobSummary = {
  id: string;
  organisation_id?: string;
  campaign_id?: string | null;
  type?: string;
  status?: string;
  total?: number;
  processed?: number;
  succeeded?: number;
  failed?: number;
  onboarded?: number;
  emailed?: number;
  metadata?: { label?: string } | Record<string, unknown>;
  campaign_name?: string | null;
};

type NotificationJobsContextValue = {
  organisationId: string | null;
  jobs: NotificationJobSummary[];
  isLoading: boolean;
  /** Invalidate active-job polling (call after enqueue). */
  registerJob: () => void;
  /** Super-admin only: scope notification banners to an organisation. */
  setSuperAdminNotificationOrgId: (id: string | null) => void;
};

const NotificationJobsContext = createContext<NotificationJobsContextValue | null>(null);

export function NotificationJobsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const role = user?.activeRole ?? user?.role ?? null;
  const [superAdminOrgId, setSuperAdminNotificationOrgId] = useState<string | null>(null);
  const canPollJobs = role === 'orgAdmin' || role === 'superAdmin';

  const organisationId = role === 'superAdmin' ? superAdminOrgId : user?.organisationId ?? null;

  const activeQuery = useQuery({
    queryKey: ['notification-jobs-active', organisationId],
    queryFn: async () => {
      const res = await notificationJobAPI.listActive(organisationId!);
      return (res.data ?? []) as NotificationJobSummary[];
    },
    enabled: canPollJobs && Boolean(organisationId),
    refetchInterval: (q) => ((q.state.data as NotificationJobSummary[] | undefined)?.length ? 2800 : 8000),
  });

  const registerJob = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['notification-jobs-active', organisationId] });
  }, [queryClient, organisationId]);

  const value = useMemo(
    () => ({
      organisationId,
      jobs: activeQuery.data ?? [],
      isLoading: activeQuery.isLoading,
      registerJob,
      setSuperAdminNotificationOrgId,
    }),
    [organisationId, activeQuery.data, activeQuery.isLoading, registerJob],
  );

  return <NotificationJobsContext.Provider value={value}>{children}</NotificationJobsContext.Provider>;
}

export function useNotificationJobs(): NotificationJobsContextValue | null {
  return useContext(NotificationJobsContext);
}
